import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

type ProjectScopedEntity = { projectId?: string | null } | null | undefined;

type ProjectContextGuardOptions = {
  entity: ProjectScopedEntity;
  isPending: boolean;
  isFetched: boolean;
  selectedProjectId?: string | null;
  setSelectedProjectId?: (projectId: string | null) => void;
  syncContextOnMismatch?: boolean;
  listPath: string;
  redirectOnMissing?: boolean;
  missingMessage?: string;
  isAllowedInWorkspace?: boolean;
};

export const useProjectContextGuard = ({
  entity,
  isPending,
  isFetched,
  selectedProjectId,
  setSelectedProjectId,
  syncContextOnMismatch = false,
  listPath,
  redirectOnMissing = false,
  missingMessage,
  isAllowedInWorkspace,
}: ProjectContextGuardOptions): void => {
  const navigate = useNavigate();

  useEffect(() => {
    if (isPending || !isFetched) return;

    if (redirectOnMissing && (!entity || isAllowedInWorkspace === false)) {
      void navigate(listPath, {
        replace: true,
        state: missingMessage ? { notice: missingMessage } : undefined,
      });
      return;
    }

    if (
      syncContextOnMismatch &&
      entity?.projectId &&
      entity.projectId !== selectedProjectId &&
      setSelectedProjectId
    ) {
      setSelectedProjectId(entity.projectId);
      return;
    }

    if (selectedProjectId && entity?.projectId && entity.projectId !== selectedProjectId) {
      void navigate(listPath, { replace: true });
    }
  }, [
    entity,
    isFetched,
    isPending,
    isAllowedInWorkspace,
    listPath,
    missingMessage,
    navigate,
    redirectOnMissing,
    selectedProjectId,
    setSelectedProjectId,
    syncContextOnMismatch,
  ]);
};
